import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import type { SynchronousQueryConfig } from 'lib/react-query';
import queryKeys from './queryKeys';
import { apiClient } from 'lib/api';
import queryString from 'query-string';

export interface AdSet {
  adgroup_id: string;
  adgroup_name: string;
}

export interface AdSetEntry {
  campaign_name: string;
  campaign_id: string;
  account_id: string;
  ad_groups: AdSet[];
}

export type CampaignRow = AdSetEntry;

export type AdGroupRow = AdSet & {
  campaign_id: string;
  campaign_name: string;
  account_id: string;
  isAdGroup: true;
};

export type GridRow = CampaignRow | AdGroupRow;

interface GetAdSetsParams {
  selectedCampaigns: string[];
  accountId: string;
}

export const getAdSets = async (
  { params }: { params: GetAdSetsParams },
  signal?: AbortSignal,
): Promise<AdSetEntry[]> => {
  const r = await apiClient.get<AdSetEntry[]>(queryKeys.adsets, {
    signal,
    params: {
      campaign_ids: params.selectedCampaigns,
      ad_account_id: params.accountId,
    },
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });

  return r.data;
};

interface UseAdSetsOptions {
  params: GetAdSetsParams;
  config?: SynchronousQueryConfig<typeof getAdSets, AdSetEntry[]>;
}

export const useAdSets = ({ params, config }: UseAdSetsOptions) => {
  return useQuery({
    staleTime: 0,
    ...config,
    retryOnMount: true,
    enabled: !!params.selectedCampaigns?.length,
    queryFn: ({ signal }) => getAdSets({ params }, signal),
    queryKey: [
      queryKeys.adsets,
      params.accountId,
      params.selectedCampaigns,
    ] as QueryKey,
  });
};
